import { create } from 'zustand';
import { API } from '../config/api';

const useVideoStore = create((set, get) => ({
  // Connection state
  isConnected: false,
  isConnecting: false,
  connectionError: null,

  // Room state
  token: null,
  roomName: null,
  livekitUrl: null,
  // Lesson context we joined with — needed so host-only actions (mute a
  // participant) can be authorized server-side without the client resending
  // full lesson info from a stale prop.
  lessonData: null,

  // Local participant state
  localParticipant: null,
  isVideoEnabled: true,
  isAudioEnabled: true,
  isScreenSharing: false,

  // Remote participants
  remoteParticipants: [],

  // UI state
  isVideoCollapsed: false,
  activeVideoView: 'gallery', // 'gallery' | 'speaker' | 'minimized'

  // Get token for joining a room. `recheck: true` asks the backend to re-probe
  // the primary LiveKit project instead of trusting its cached health — used
  // after a failed connect so we land on the backup project if the primary
  // just ran out of minutes.
  fetchToken: async (lessonData, { recheck = false } = {}) => {
    set({ isConnecting: true, connectionError: null });
    try {
      const res = await fetch(`${API.LIVEKIT}/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // This sends httpOnly cookies automatically
        body: JSON.stringify({ lessonData, recheck }),
      });

      const data = await res.json();

      if (res.ok) {
        set({
          token: data.token,
          roomName: data.roomName,
          livekitUrl: data.livekitUrl,
          localParticipant: data.participant,
          lessonData,
          isConnecting: false,
        });
        return { success: true, ...data };
      } else {
        set({ connectionError: data.error, isConnecting: false });
        return { success: false, error: data.error };
      }
    } catch (err) {
      set({ connectionError: err.message, isConnecting: false });
      return { success: false, error: err.message };
    }
  },

  // Called when successfully connected to room
  setConnected: (connected) => {
    set({ isConnected: connected, connectionError: null });
  },

  // Toggle video
  setVideoEnabled: (enabled) => {
    set({ isVideoEnabled: enabled });
  },

  // Toggle audio
  setAudioEnabled: (enabled) => {
    set({ isAudioEnabled: enabled });
  },

  // Toggle screen share
  setScreenSharing: (sharing) => {
    set({ isScreenSharing: sharing });
  },

  // Update remote participants list
  setRemoteParticipants: (participants) => {
    set({ remoteParticipants: participants });
  },

  // Host-only: force-mute a remote participant's camera or microphone.
  // Server verifies the caller is the room host — the client can call this
  // optimistically without pre-checking permissions.
  muteParticipantTrack: async (participantIdentity, source) => {
    const { lessonData } = get();
    if (!lessonData) {
      return { success: false, error: 'No active lesson context' };
    }
    try {
      const res = await fetch(`${API.LIVEKIT}/mute-participant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ lessonData, participantIdentity, source }),
      });
      const data = await res.json();
      if (!res.ok) {
        return { success: false, error: data.error || 'Failed to mute participant' };
      }
      return { success: true, ...data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // Toggle video panel collapsed state
  toggleVideoCollapsed: () => {
    set(state => ({ isVideoCollapsed: !state.isVideoCollapsed }));
  },

  // Set video view mode
  setActiveVideoView: (view) => {
    set({ activeVideoView: view });
  },

  // Disconnect and reset state
  disconnect: () => {
    set({
      isConnected: false,
      isConnecting: false,
      connectionError: null,
      token: null,
      roomName: null,
      localParticipant: null,
      lessonData: null,
      remoteParticipants: [],
      isVideoEnabled: true,
      isAudioEnabled: true,
      isScreenSharing: false,
    });
  },

  // Reset all state (for cleanup)
  reset: () => {
    set({
      isConnected: false,
      isConnecting: false,
      connectionError: null,
      token: null,
      roomName: null,
      livekitUrl: null,
      localParticipant: null,
      lessonData: null,
      remoteParticipants: [],
      isVideoEnabled: true,
      isAudioEnabled: true,
      isScreenSharing: false,
      isVideoCollapsed: false,
      activeVideoView: 'gallery',
    });
  },
}));

export default useVideoStore;
